import { Component, h } from 'preact'
import Panel from '../panel'
import IntegrationHeader from './IntegrationHeader'
import ButtonControl from '../button-control'
import axios from 'axios'
import InputGroup from '../ui-components/InputGroup'
import AlertComponent from '../ui-components/AlertComponent'
import ConfirmationModal from '../ui-components/ConfirmationModal'
import globals from '../../utils/globals'
import { isHtmlElement } from '../../libs/typeGuards'
import Header from '../header'

interface Props {
 csrfToken: string
 isIntegrationsHomepage: boolean
}

interface GainsightData {
  bucketName: string,
  accessKey: string,
  securityToken: string,
}

interface State extends GainsightData {
  isLoading?: boolean,
  isInstalled: boolean,
  messageType?: string|null,
  message?: string,
  showConfirmationMessage: boolean
}

class GainsightIntegration extends Component<Props, State> {
  headerDescription: string
  isGainsightEnabled: boolean

  constructor (props: Props) {
    super(props)
    this.state = {
      isLoading: false,
      bucketName: '',
      accessKey: '',
      securityToken: '',
      isInstalled: false,
      messageType: null,
      message: '',
      showConfirmationMessage: false
    }
    this.headerDescription = 'Boost customer success with a 360-degree view of your community members & their activity in Gainsight'
    this.isGainsightEnabled = ((globals('activeModules') || []).indexOf('gainsight') !== -1)
  }

  async componentDidMount () {
    if (!this.isGainsightEnabled) {
      return
    }
    this.setState({ isLoading: true, messageType: null })
    try {
      const res = await axios.get('/integration/gainsight/settings', {
        validateStatus: (state: number) => {
          return (state >= 200 && state < 300) || state === 403
        }
      })
      if (Object.keys(res.data).length > 0) {
        this.setState({
          bucketName: res.data.bucketPath,
          accessKey: res.data.accessKey,
          securityToken: res.data.securityToken,
          isInstalled: true
        })
      }
      this.setState({ isLoading: false })
    } catch (e) {
      this.setState({ messageType: 'error', message: 'Unable to retrieve gainsight configuration.', isLoading: false })
    }
  }

  handleOpenEmbeddables () {
    const embeddablesButton = document.querySelector('.insided-conversational-trigger-button')

    if (isHtmlElement(embeddablesButton)) {
      embeddablesButton.click()
    }
  }

  async onDelete () {
    this.setState({ isLoading: true, messageType: null })
    const errorMessage = 'Unable to delete credentials for gainsight integration.'
    try {
      await axios.delete('/integration/gainsight/settings', { data: { csrfToken: this.props.csrfToken } })
      this.setState({
        bucketName: '',
        accessKey: '',
        securityToken: '',
        isLoading: false,
        showConfirmationMessage: false,
        isInstalled: false
      })
    } catch (e) {
      this.setState({ messageType: 'error', message: errorMessage, isLoading: false, showConfirmationMessage: false })
    }
  }

  async onSave () {
    this.setState({ messageType: null, isLoading: true })
    const successMessage = 'Gainsight integration is installed successfully!'
    let errorMessage: string = 'Unable to save configuration for gainsight integration.'
    try {
      await axios.post('/integration/gainsight/settings', {
        bucketPath: this.state.bucketName,
        accessKey: this.state.accessKey,
        securityToken: this.state.securityToken,
        csrfToken: this.props.csrfToken
      })
      this.setState({ isInstalled: true, messageType: 'success', message: successMessage, isLoading: false })
    } catch (e) {
      if (e.response.data) {
        errorMessage = e.response.data.error.reduce((accum: string, item: string) => {
          accum += '<br>' + item
          return accum
        })
      }

      this.setState({ messageType: 'error', message: errorMessage, isLoading: false })
    }
  }

  render (props: Props, state: State) {
    const isVisibleFor = state.messageType === 'success' ? 5000 : false
    const getDeleteConfirmationButtons = () => {
      return [
        {
          text: 'Confirm',
          class: 'btn btn-danger qa-confirm',
          showLoading: true,
          callback: () => this.onDelete()
        },
        {
          text: 'Cancel',
          class: 'btn btn-default',
          showLoading: false,
          callback: () => this.setState({ showConfirmationMessage: false })
        }
      ]
    }

    if (props.isIntegrationsHomepage) {
      return (
        <div class='integrations-gainsight'>
          <Panel title={<IntegrationHeader isInstalled={state.isInstalled} name='Gainsight'
          description={this.headerDescription}><img style={{ width: '56px' }} src={`${globals('basePath')}images/gainsight-logo.png`} /></IntegrationHeader>}></Panel>
        </div>
      )
    }

    return (
      <div class='integrations-gainsight'>
        {!props.isIntegrationsHomepage &&
          <Header
            title='Gainsight'
            disableCancelButton
            disableSaveButton
            hasBreadcrumb
            breadcrumbText='Back to integrations overview'
            breadcrumbLink='/settings/integrations'
            statusLabelText={state.isInstalled ? 'Installed' : ''}
            statusLabelSuccess
          />
        }
        <Panel title='Integration setup'>
          {
            state.messageType ? <AlertComponent messageType={state.messageType} message={state.message} sticky renderHTML isVisibleFor={isVisibleFor} /> : null
          }
          {!this.isGainsightEnabled
            ? (<div className='qa-gainsight-integration-disabled'> The Gainsight integration is part of our
                Enterprise package. Please contact your CSM (or Support) to learn more. </div>)
            : (<div>
              <p className='integrations-gainsight__info'>
                Learn how to install the Gainsight integration step by
                  step. <button className='gainsight-configuration__note-text-btn' type='button' onClick={() => this.handleOpenEmbeddables()}>Learn more</button>
              </p>
              <p className='integrations-gainsight__info'>
                This configuration uses the Gainsight-managed S3 bucket and is intended for customers without Salesforce. If you use Salesforce, we recommend using our Salesforce integration as a data source in Gainsight.
              </p>
              <form className='integrations-gainsight__form' onSubmit={(e) => { e.preventDefault(); this.onSave() }}>
                <InputGroup title='Bucket access path *'
                  type='text'
                  required
                  readOnly={state.isInstalled || state.isLoading ? 'readonly' : ''}
                  value={state.bucketName}
                  onChange={(e) => this.setState({ bucketName: e.target.value })}
                  qaClasses='qa-gainsight-username-input' />
                <InputGroup title='Access key *'
                  type='text'
                  required
                  readOnly={state.isInstalled || state.isLoading ? 'readonly' : ''}
                  value={state.accessKey}
                  onChange={(e) => this.setState({ accessKey: e.target.value })}
                  qaClasses='qa-gainsight-instance-url-input' />
                <InputGroup title='Security token *'
                  type='text'
                  required
                  readOnly={state.isInstalled || state.isLoading ? 'readonly' : ''}
                  value={state.securityToken}
                  onChange={(e) => this.setState({ securityToken: e.target.value })}
                  qaClasses='qa-gainsight-app-consumer-key-input' />
                {state.isInstalled ? <ButtonControl variant='warning' class={'qa-delete'} title='Uninstall and delete credentials' onClick={() => this.setState({ showConfirmationMessage: true })} />
                  : <ButtonControl variant='primary' type='submit' class={'qa-gainsight-submit'} title='Install' isLoading={state.isLoading} />}
              </form>
              <ConfirmationModal
                isLoading={state.isLoading}
                enableModal={state.showConfirmationMessage}
                title='Uninstall Gainsight integration?'
                message='Community data will no longer be pushed to your Gainsight instance.'
                buttons={getDeleteConfirmationButtons()}
                onClose={() => this.setState({ showConfirmationMessage: false })}
              />
            </div>)
          }
        </Panel>
      </div>
    )
  }
}

export default GainsightIntegration
